import { Transition } from '@headlessui/react';
import {
  StructuredText as IStructuredText,
  isList,
  isListItem,
  isParagraph,
} from 'datocms-structured-text-utils';
import React, { FC, useState } from 'react';
import { StructuredText, renderRule } from 'react-datocms';
import { useInView } from 'react-intersection-observer';

import { IImage } from '../../types/module';

import '../../sass/styles.scss';

import {
  textBody,
  textBodyLarge,
  textBodyUppercaseSmall,
} from '../../../../growth-platform-brand-system-v2/src/styles/typography';
import { classNames } from '../../../../growth-platform-brand-system-v2/src/utils/style';
import { useScreenSize } from '../../../../growth-platform-brand-system-v2/src/utils/utils';
import { applyBgColor } from './logic';
import { Image } from '@payfit/growth-platform-brand-system-v2';

export interface ValueCardProps {
  illustration?: IImage;
  surtitle?: string;
  title?: string;
  description?: IStructuredText;
  color?: 'azur' | 'blue' | 'coral' | 'mint' | 'neutral' | 'purple' | 'yellow';
  className?: string;
}

export const ValueCard: FC<ValueCardProps> = ({
  illustration,
  surtitle,
  title,
  description,
  color = 'blue',
  className,
}) => {
  const [hovered, setHovered] = useState(false);
  const screenSize = useScreenSize();
  const { ref, inView } = useInView({
    threshold: 0.7,
  });
  const displayText = hovered || (screenSize === 'xs' && inView);

  return (
    <div
      className={classNames(
        'ValueCard',
        'group value-card overflow-hidden bg-neutral-1 flex flex-col border rounded-18 border-neutral-51',
        className,
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="px-32 py-32">
        {surtitle && (
          <div
            className={classNames(
              textBodyUppercaseSmall,
              'mb-24 text-neutral-101',
            )}
          >
            {surtitle}
          </div>
        )}
        {title && (
          <h3
            className={classNames(
              textBodyLarge,
              'font-medium  text-neutral-301',
            )}
          >
            {title}
          </h3>
        )}
      </div>
      <div
        className={`${applyBgColor(
          color,
          inView,
          screenSize,
        )} transition-all duration-500 h-full`}
        ref={ref}
      >
        {!displayText && illustration?.url && (
          <div className="flex items-center justify-center h-full">
            <Image
              alt={illustration?.alt || ''}
              src={illustration?.url}
              className="w-full h-full"
              sizes={'100px'}
              pixelDensity={'image'}
              width={illustration?.width || 0}
              height={illustration?.height || 0}
            />
          </div>
        )}
        <Transition
          show={displayText}
          enter="transition-opacity duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
        >
          <div className={classNames(textBody, 'px-32 py-32 text-neutral-101')}>
            <StructuredText
              data={description}
              customNodeRules={[
                renderRule(isParagraph, ({ children, key, ancestors }) => {
                  const isAncestorListItem =
                    ancestors?.[0]?.type === 'listItem';

                  return (
                    <p
                      key={key}
                      className={
                        isAncestorListItem
                          ? `lg:line-clamp-6 xl:line-clamp-none`
                          : ''
                      }
                    >
                      {children}
                    </p>
                  );
                }),
                renderRule(isList, ({ children, key }) => (
                  <ul key={key} className={'list-disc list-outside ml-1em'}>
                    {children}
                  </ul>
                )),
                renderRule(isListItem, ({ children, key }) => {
                  return <li key={key}>{children}</li>;
                }),
              ]}
            />
          </div>
        </Transition>
      </div>
    </div>
  );
};
